<template>
  <div class="custom-select-container" :class="{ 'is-invalid': errorMessage }">
    <div
      class="form-select-wrapper"
      :class="{ 'is-invalid': errorMessage }"
    >
      <div class="select-inner-wrapper">
        <slot name="prefix" v-if="$slots.prefix" class="prefix-content"></slot>
        
        <select
          :id="id || name"
          :name="name"
          :placeholder="placeholder"
          :value="modelValue"
          class="form-select h-[36px]"
          :class="{ 
            'is-invalid': errorMessage,
            'form-select-sm': size === 'sm',
            'form-select-lg': size === 'lg',
            'with-prefix': $slots.prefix,
            'with-suffix': $slots.suffix || (clearable && (modelValue || modelValue === 0)),
            'placeholder-shown': modelValue === ''
          }"
          :style="{
            width,
            ...$attrs.style
          }"
          :disabled="disabled"
          @change="handleChange"
          @blur="handleBlur"
          @focus="handleFocus"
          @mousedown.prevent.stop="toggleDropdown"
          @touchstart.prevent.stop="toggleDropdown"
          ref="selectEl"
        >
          <option style="display: none;" v-if="placeholder" value="" disabled selected>{{ placeholder }}</option>
          <option 
            v-for="option in options" 
            :key="option.value" 
            :value="option.value" 
            :disabled="option.disabled"
            style="display: none;"
          >
            {{ option.label }}
          </option>
        </select>
        
        <div class="suffix-container" v-if="$slots.suffix || (clearable && (modelValue || modelValue === 0))">
          <div v-if="clearable && (modelValue || modelValue === 0)" class="clear-btn" @click.stop="clearSelect">
            <SvgIcon iconName="icon-icon_universe_close_fill" class="me-1"></SvgIcon>
          </div>
          <slot name="suffix" v-if="$slots.suffix"></slot>
        </div>
      </div>
    </div>
    
    <Teleport to="body">
      <div class="scanat-vsb-menu" v-show="isOpen" ref="dropdown" :style="dropdownStyle">
        <ul>
          <li 
            v-for="option in options" 
            :key="option.value" 
            :class="{ 
              'active': modelValue === option.value,
              'disabled': option.disabled 
            }"
            @click="selectOption(option)"
          >
            {{ option.label }}
          </li>
          <slot></slot>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, inject, provide, Teleport } from 'vue'
import SvgIcon from '../../common/SvgIcon.vue'

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  width: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [String, Number, Boolean, Array],
    default: ''
  },
  name: {
    type: String,
    required: true
  },
  options: {
    type: Array,
    default: () => []
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: '',
    validator: (value) => ['sm', 'lg', ''].includes(value)
  },
  validateOnChange: {
    type: Boolean,
    default: true
  },
  clearable: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'change', 'clear'])

const formContext = inject('formContext', null)
const isNested = inject('isFormItemNested', false)
provide('isFormItemNested', true)

const isOpen = ref(false)
const selectEl = ref(null)
const dropdown = ref(null)
const dropdownStyle = ref({
  top: '0px',
  left: '0px',
  width: 'auto'
})

const errorMessage = computed(() => {
  if (!formContext || !formContext.errors) return ''
  return formContext.errors[props.name] || ''
})

const coerceSelectValue = (rawValue) => {
  if (rawValue === '') return rawValue
  if (typeof rawValue !== 'string') return rawValue
  if (!/^-?\d+$/.test(rawValue)) return rawValue
  const num = Number(rawValue)
  return Number.isNaN(num) ? rawValue : num
}

const toggleDropdown = (event) => {
  if (props.disabled) return
  
  // 阻止原生select的下拉菜单显示
  event.preventDefault()
  
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    nextTick(() => {
      positionDropdown()
      document.addEventListener('click', closeOnClickOutside)
    })
  } else {
    document.removeEventListener('click', closeOnClickOutside)
  }
}

const handleFocus = (event) => {
  event.preventDefault()
  emit('focus', event)
}

const closeOnClickOutside = (event) => {
  if (selectEl.value && !selectEl.value.contains(event.target) && 
      dropdown.value && !dropdown.value.contains(event.target)) {
    isOpen.value = false
    document.removeEventListener('click', closeOnClickOutside)
  }
}

const positionDropdown = () => {
  if (!dropdown.value || !selectEl.value) return

  const selectRect = selectEl.value.getBoundingClientRect()

  // 计算下拉框的高度（先渲染获取实际高度）
  dropdown.value.style.visibility = 'hidden'
  dropdown.value.style.display = 'block'
  const dropdownRect = dropdown.value.getBoundingClientRect()
  dropdown.value.style.visibility = 'visible'
  dropdown.value.style.display = ''

  const dropdownHeight = dropdownRect.height
  const dropdownWidth = dropdownRect.width

  // 计算可用空间
  const spaceBelow = window.innerHeight - selectRect.bottom
  const spaceAbove = selectRect.top

  // 判断是否应该显示在上方
  let shouldFlip = false
  if (dropdownHeight > spaceBelow) {
    // 下方空间不足，检查上方是否有足够空间
    if (spaceAbove >= dropdownHeight) {
      shouldFlip = true
    } else if (spaceAbove > spaceBelow) {
      // 上下都不足，但上方空间更大，还是向上
      shouldFlip = true
    }
  }

  // 计算垂直位置
  let top = shouldFlip
    ? selectRect.top - dropdownHeight - 4
    : selectRect.bottom + 4

  // 计算水平位置（考虑边界）
  let left = selectRect.left
  const maxLeft = window.innerWidth - dropdownWidth - 8
  if (left > maxLeft) {
    left = maxLeft
  }

  dropdownStyle.value = {
    width: `${Math.max(selectRect.width, dropdownWidth)}px`,
    left: `${left + window.scrollX}px`,
    top: `${top + window.scrollY}px`
  }
}

const selectOption = (option) => {
  if (option.disabled) return
  
  emit('update:modelValue', option.value)
  emit('change', option.value)
  isOpen.value = false
  
  if (props.validateOnChange && formContext && formContext.validateField) {
    formContext.validateField(props.name)
  }
}

const handleChange = (event) => {
  const value = coerceSelectValue(event.target.value)
  emit('update:modelValue', value)
  emit('change', value)
  
  if (props.validateOnChange && formContext && formContext.validateField) {
    formContext.validateField(props.name)
  }
}

const handleBlur = (event) => {
  emit('blur', event)
}

const clearSelect = (event) => {
  event.stopPropagation()
  emit('update:modelValue', '')
  emit('clear')
  emit('change', '')
}

const handleResize = () => {
  if (isOpen.value) {
    positionDropdown()
  }
}

const handleScroll = () => {
  if (isOpen.value) {
    positionDropdown()
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
  window.addEventListener('scroll', handleScroll, true)
  
  if (formContext && formContext.registerFormItem) {
    formContext.registerFormItem(props.name, {
      validate: () => formContext.validateField(props.name)
    })
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleScroll, true)
  document.removeEventListener('click', closeOnClickOutside)
  
  if (formContext && formContext.unregisterFormItem) {
    formContext.unregisterFormItem(props.name)
  }
})

watch(() => props.modelValue, () => {
  if (props.validateOnChange && formContext && formContext.validateField) {
    formContext.validateField(props.name)
  }
})
</script>

<style scoped>
.custom-select-container {
    position: relative;
}
.form-select-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
}

.select-inner-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.prefix-content {
  position: absolute;
  left: 0.75rem;
  z-index: 2;
}

.form-select {
  background-color: #F5FAFF !important;
  border-color: #D8E8FF;
  font-size: 0.875rem;
  color: #343b46;
  width: 100%;
  transition: all .2s linear;
}

.form-select:focus {
  border-color: #3b71ca;
  outline: 0;
  box-shadow: inset 0 0 0 1px #3b71ca;
}

.form-select option[value=""][disabled] {
  color: #6c757d;
}

.form-select.with-prefix {
  padding-left: 2.5rem;
}

.form-select.with-suffix {
  padding-right: 2.5rem;
}

.form-select.is-invalid {
  border-color: #C62741 !important;
}

.form-select.is-invalid:focus {
  border-color: #C62741 !important;
  box-shadow: none;
}
.form-select[disabled] {
    background-color: #ECEFF2 !important;
    border: none
}

.form-select.placeholder-shown {
  color: #8697aa;
}

.scanat-vsb-menu {
  z-index: 2000;
  display: block;
  visibility: hidden;
  position: fixed;
  width: 100%;
}

.scanat-vsb-menu ul {
  user-select: none;
  list-style: none;
  white-space: nowrap;
  margin: 0;
  padding: 0.5rem 0;
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.375rem;
  letter-spacing: 0;
  color: #343b46;
  background-color: #fff;
  background-clip: padding-box;
  box-shadow: 0px 0px 24px 0px #0000001a;
  border-radius: 4px;
  cursor: pointer;
  overflow-y: auto;
  max-height: 250px;
}

.scanat-vsb-menu ul li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
  height: 2.25rem;
}

.scanat-vsb-menu ul li:hover:not(.disabled) {
  background: #0068B00D;
}

.scanat-vsb-menu ul li.active {
  color: #00365b;
  background: #0068B014;
}

.scanat-vsb-menu ul li.active::after {
  content: '';
  width: 1rem;
  height: 1rem;
  background-image: url("/src/assets/icons/correct.svg");
  background-position: center;
  background-repeat: no-repeat;
}

.scanat-vsb-menu ul li.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.suffix-container {
  position: absolute;
  right: 1.75rem;
  display: flex;
  align-items: center;
  z-index: 2;
  gap: 0.5rem; 
}

.clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.25rem;
  background: none;
  border: none;
  opacity: 0;
  transition: opacity 0.2s;
}

.form-select-wrapper:hover .clear-btn {
  opacity: 1;
}

.clear-btn:hover svg {
  fill: #0068B0;
}

.invalid-feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #C62741;
}
</style> 